/**
 * JSON-LD builders for SEO Discovery venue + city pages.
 *
 * Returns arrays of top-level schema objects (each with @context) so Page.js
 * can merge them with Organization and Rich Results / FAQ validators can see
 * FAQPage at the root of an entry — not only nested under @graph.
 */

const SCHEMA_CONTEXT = 'https://schema.org';

const stripHtml = s =>
  String(s || '')
    .replace(/<[^>]+>/g, '')
    .trim();

const absoluteUrl = (rootUrl, pathOrUrl) => {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const root = String(rootUrl || '').replace(/\/$/, '');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${root}${path}`;
};

/** FAQs shown on the page — mark them all up unless explicitly ineligible. */
export const extractFaqsFromBlocks = contentBlocks => {
  const faqBlock = (contentBlocks || []).find(b => b.blockType === 'faq');
  return faqBlock?.content?.faqs || [];
};

export const buildBreadcrumbListSchema = (breadcrumbs, rootUrl) => {
  const items = (breadcrumbs || [])
    .filter(b => b.path)
    .map((b, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: b.name,
      item: absoluteUrl(rootUrl, b.path),
    }));
  if (!items.length) {
    return null;
  }
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

export const buildVenuePlaceSchema = ({ venue, rootUrl, canonicalPath }) => {
  if (!venue?.venueName) {
    return null;
  }
  const schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'EventVenue',
    '@id': `${absoluteUrl(rootUrl, canonicalPath)}#venue`,
    name: venue.venueName,
    url: absoluteUrl(rootUrl, canonicalPath),
  };
  if (venue.image?.url) {
    schema.image = absoluteUrl(rootUrl, venue.image.url);
  }
  if (venue.address || venue.city || venue.state) {
    schema.address = {
      '@type': 'PostalAddress',
      ...(venue.address ? { streetAddress: venue.address } : {}),
      ...(venue.city ? { addressLocality: venue.city } : {}),
      ...(venue.state ? { addressRegion: venue.state } : {}),
      addressCountry: venue.country || 'US',
    };
  }
  if (venue.latitude != null && venue.longitude != null) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: venue.latitude,
      longitude: venue.longitude,
    };
  }
  if (venue.capacity) {
    schema.maximumAttendeeCapacity = venue.capacity;
  }
  return schema;
};

/**
 * FAQPage for visible Q&A. Matches on-page FAQ section.
 * Excludes only entries explicitly marked schemaEligible === false.
 */
export const buildFaqPageSchema = faqs => {
  const eligible = (faqs || []).filter(
    f => f && f.schemaEligible !== false && stripHtml(f.question) && stripHtml(f.answer)
  );
  if (!eligible.length) {
    return null;
  }
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'FAQPage',
    mainEntity: eligible.map(f => ({
      '@type': 'Question',
      name: stripHtml(f.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(f.answer),
      },
    })),
  };
};

export const buildUpcomingEventsItemListSchema = ({
  upcomingEvents,
  rootUrl,
  name = 'Upcoming events',
} = {}) => {
  const items = (upcomingEvents || [])
    .filter(ev => ev.path || ev.url)
    .slice(0, 12)
    .map((ev, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: absoluteUrl(rootUrl, ev.path || ev.url),
      name: ev.title || ev.name,
    }));
  if (!items.length) {
    return null;
  }
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    name,
    itemListElement: items,
  };
};

export const buildVenuesItemListSchema = ({ venues, rootUrl, name = 'Venues' } = {}) => {
  const items = (venues || [])
    .filter(v => v.path || v.url || v.slug)
    .slice(0, 24)
    .map((v, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: absoluteUrl(
        rootUrl,
        v.path || v.url || `/event-discovery/venues/${v.slug}/`
      ),
      name: v.venueName || v.name,
    }));
  if (!items.length) {
    return null;
  }
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    name,
    itemListElement: items,
  };
};

export const buildCityPlaceSchema = ({ city, rootUrl, canonicalPath }) => {
  if (!city?.city) {
    return null;
  }
  const schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'City',
    '@id': `${absoluteUrl(rootUrl, canonicalPath)}#city`,
    name: city.city,
    url: absoluteUrl(rootUrl, canonicalPath),
  };
  if (city.state) {
    schema.containedInPlace = {
      '@type': 'State',
      name: city.state,
    };
  }
  if (city.country || city.state) {
    schema.address = {
      '@type': 'PostalAddress',
      addressLocality: city.city,
      ...(city.state ? { addressRegion: city.state } : {}),
      addressCountry: city.country || 'US',
    };
  }
  if (city.image?.url) {
    schema.image = absoluteUrl(rootUrl, city.image.url);
  }
  return schema;
};

const buildWebPageSchema = ({
  '@type': pageType = 'WebPage',
  name,
  description,
  url,
  image,
  about,
  mainEntity,
}) => {
  if (!name || !url) {
    return null;
  }
  const schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': pageType,
    name,
    url,
  };
  if (description) schema.description = description;
  if (image) schema.image = image;
  if (about) schema.about = about;
  if (mainEntity) schema.mainEntity = mainEntity;
  return schema;
};

/**
 * Venue hub schema: WebPage, EventVenue, BreadcrumbList, FAQPage, upcoming ItemList.
 */
export const buildVenuePageSchema = ({
  venue,
  canonicalPath,
  rootUrl,
  breadcrumbs,
  faqs,
  upcomingEvents,
  meta,
}) => {
  const pageUrl = absoluteUrl(rootUrl, canonicalPath);
  const venueSchema = buildVenuePlaceSchema({ venue, rootUrl, canonicalPath });
  const image =
    venue?.image?.url && absoluteUrl(rootUrl, venue.image.url)
      ? absoluteUrl(rootUrl, venue.image.url)
      : undefined;

  return [
    buildWebPageSchema({
      name: meta?.metaTitle || venue?.venueName,
      description: meta?.metaDescription,
      url: pageUrl,
      image,
      about: venueSchema ? { '@id': venueSchema['@id'] } : undefined,
      mainEntity: venueSchema ? { '@id': venueSchema['@id'] } : undefined,
    }),
    venueSchema,
    buildBreadcrumbListSchema(breadcrumbs, rootUrl),
    buildFaqPageSchema(faqs),
    buildUpcomingEventsItemListSchema({
      upcomingEvents,
      rootUrl,
      name: venue?.venueName ? `Upcoming events at ${venue.venueName}` : 'Upcoming events',
    }),
  ].filter(Boolean);
};

/**
 * Venue informational / events subpage schema.
 */
export const buildVenueSubpageSchema = ({
  venue,
  venueHubPath,
  canonicalPath,
  rootUrl,
  breadcrumbs,
  faqs,
  upcomingEvents,
  meta,
  includeUpcomingEvents = false,
}) => {
  const pageUrl = absoluteUrl(rootUrl, canonicalPath);
  const hubPath = venueHubPath || `/event-discovery/venues/${venue?.slug}/`;
  const venueSchema = buildVenuePlaceSchema({
    venue,
    rootUrl,
    canonicalPath: hubPath,
  });
  const image =
    venue?.image?.url && absoluteUrl(rootUrl, venue.image.url)
      ? absoluteUrl(rootUrl, venue.image.url)
      : undefined;

  return [
    buildWebPageSchema({
      name: meta?.metaTitle || meta?.h1 || venue?.venueName,
      description: meta?.metaDescription,
      url: pageUrl,
      image,
      about: venueSchema ? { '@id': venueSchema['@id'] } : undefined,
    }),
    venueSchema,
    buildBreadcrumbListSchema(breadcrumbs, rootUrl),
    buildFaqPageSchema(faqs),
    includeUpcomingEvents
      ? buildUpcomingEventsItemListSchema({
          upcomingEvents,
          rootUrl,
          name: venue?.venueName ? `Upcoming events at ${venue.venueName}` : 'Upcoming events',
        })
      : null,
  ].filter(Boolean);
};

/**
 * City hub schema: CollectionPage, City, BreadcrumbList, FAQPage, venue + event lists.
 */
export const buildCityPageSchema = ({
  city,
  canonicalPath,
  rootUrl,
  breadcrumbs,
  faqs,
  venues,
  upcomingEvents,
  meta,
}) => {
  const pageUrl = absoluteUrl(rootUrl, canonicalPath);
  const citySchema = buildCityPlaceSchema({ city, rootUrl, canonicalPath });
  const image =
    city?.image?.url && absoluteUrl(rootUrl, city.image.url)
      ? absoluteUrl(rootUrl, city.image.url)
      : undefined;
  const cityName = city?.city || 'this city';

  return [
    buildWebPageSchema({
      '@type': 'CollectionPage',
      name: meta?.metaTitle || meta?.h1 || `Events in ${cityName}`,
      description: meta?.metaDescription || meta?.description,
      url: pageUrl,
      image,
      about: citySchema ? { '@id': citySchema['@id'] } : undefined,
      mainEntity: citySchema ? { '@id': citySchema['@id'] } : undefined,
    }),
    citySchema,
    buildBreadcrumbListSchema(breadcrumbs, rootUrl),
    buildFaqPageSchema(faqs),
    buildVenuesItemListSchema({
      venues,
      rootUrl,
      name: `Venues in ${cityName}`,
    }),
    buildUpcomingEventsItemListSchema({
      upcomingEvents,
      rootUrl,
      name: `Upcoming events in ${cityName}`,
    }),
  ].filter(Boolean);
};

/**
 * City spoke (upcoming events / venues directory) schema.
 */
export const buildCitySubpageSchema = ({
  city,
  cityHubPath,
  canonicalPath,
  rootUrl,
  breadcrumbs,
  faqs,
  venues,
  upcomingEvents,
  meta,
  pageKey,
}) => {
  const pageUrl = absoluteUrl(rootUrl, canonicalPath);
  const hubPath = cityHubPath || `/event-discovery/cities/${city?.citySlug || ''}/`;
  const citySchema = buildCityPlaceSchema({
    city,
    rootUrl,
    canonicalPath: hubPath,
  });
  const cityName = city?.city || 'this city';
  const isEvents = pageKey === 'upcoming-events' || pageKey === 'events';

  return [
    buildWebPageSchema({
      '@type': 'CollectionPage',
      name: meta?.metaTitle || meta?.h1 || cityName,
      description: meta?.metaDescription || meta?.description,
      url: pageUrl,
      about: citySchema ? { '@id': citySchema['@id'] } : undefined,
    }),
    citySchema,
    buildBreadcrumbListSchema(breadcrumbs, rootUrl),
    buildFaqPageSchema(faqs),
    isEvents
      ? buildUpcomingEventsItemListSchema({
          upcomingEvents,
          rootUrl,
          name: `Upcoming events in ${cityName}`,
        })
      : buildVenuesItemListSchema({
          venues,
          rootUrl,
          name: `Venues in ${cityName}`,
        }),
  ].filter(Boolean);
};

/**
 * Only return MusicGroup|SportsTeam|PerformingGroup|Person when evidenced.
 * Never guess type from the performer name alone.
 */
const EVIDENCED_PERFORMER_TYPES = new Set([
  'MusicGroup',
  'SportsTeam',
  'PerformingGroup',
  'Person',
]);

export const resolvePerformerSchemaType = ({
  performerType,
  schemaType,
  evidence,
} = {}) => {
  if (evidence == null || evidence === false) {
    return null;
  }
  if (Array.isArray(evidence) && evidence.length === 0) {
    return null;
  }
  const fromEvidence =
    evidence && typeof evidence === 'object' && !Array.isArray(evidence)
      ? evidence.schemaType || evidence.performerType || null
      : null;
  const candidate = fromEvidence || schemaType || performerType;
  if (!candidate) {
    return null;
  }
  const type = String(candidate).trim();
  return EVIDENCED_PERFORMER_TYPES.has(type) ? type : null;
};

export const buildPerformerEntitySchema = ({
  performer,
  rootUrl,
  canonicalPath,
  schemaType,
}) => {
  if (!performer?.performerName && !performer?.name) {
    return null;
  }
  const name = performer.performerName || performer.name;
  const type = schemaType || null;
  const schema = {
    '@context': SCHEMA_CONTEXT,
    ...(type ? { '@type': type } : { '@type': 'Thing' }),
    '@id': `${absoluteUrl(rootUrl, canonicalPath)}#performer`,
    name,
    url: absoluteUrl(rootUrl, canonicalPath),
  };
  if (performer.image?.url) {
    schema.image = absoluteUrl(rootUrl, performer.image.url);
  }
  return schema;
};

/**
 * Performer hub schema: CollectionPage + optional evidenced entity type.
 * When type is not evidenced, mainEntity is omitted (CollectionPage only).
 */
export const buildPerformerPageSchema = ({
  performer,
  canonicalPath,
  rootUrl,
  breadcrumbs,
  faqs,
  venues,
  upcomingEvents,
  meta,
  evidence,
}) => {
  const pageUrl = absoluteUrl(rootUrl, canonicalPath);
  const resolvedType = resolvePerformerSchemaType({
    performerType: performer?.performerType,
    schemaType: performer?.schemaType,
    evidence: evidence ?? performer?.schemaEvidence ?? null,
  });
  const performerSchema = resolvedType
    ? buildPerformerEntitySchema({
        performer,
        rootUrl,
        canonicalPath,
        schemaType: resolvedType,
      })
    : null;
  const image =
    performer?.image?.url && absoluteUrl(rootUrl, performer.image.url)
      ? absoluteUrl(rootUrl, performer.image.url)
      : undefined;
  const name = performer?.performerName || performer?.name || 'this performer';

  return [
    buildWebPageSchema({
      '@type': 'CollectionPage',
      name: meta?.metaTitle || meta?.h1 || `${name} Tickets`,
      description: meta?.metaDescription || meta?.description,
      url: pageUrl,
      image,
      about: performerSchema ? { '@id': performerSchema['@id'] } : undefined,
      mainEntity: performerSchema ? { '@id': performerSchema['@id'] } : undefined,
    }),
    performerSchema,
    buildBreadcrumbListSchema(breadcrumbs, rootUrl),
    buildFaqPageSchema(faqs),
    buildVenuesItemListSchema({
      venues,
      rootUrl,
      name: `Venues for ${name}`,
    }),
    buildUpcomingEventsItemListSchema({
      upcomingEvents,
      rootUrl,
      name: `Upcoming ${name} events`,
    }),
  ].filter(Boolean);
};

/**
 * Performer spoke (upcoming events / venues played) schema.
 */
export const buildPerformerSubpageSchema = ({
  performer,
  performerHubPath,
  canonicalPath,
  rootUrl,
  breadcrumbs,
  faqs,
  venues,
  upcomingEvents,
  meta,
  pageKey,
  evidence,
}) => {
  const pageUrl = absoluteUrl(rootUrl, canonicalPath);
  const hubPath =
    performerHubPath ||
    `/event-discovery/performers/${performer?.performerSlug || performer?.slug || ''}/`;
  const resolvedType = resolvePerformerSchemaType({
    performerType: performer?.performerType,
    schemaType: performer?.schemaType,
    evidence: evidence ?? performer?.schemaEvidence ?? null,
  });
  const performerSchema = resolvedType
    ? buildPerformerEntitySchema({
        performer,
        rootUrl,
        canonicalPath: hubPath,
        schemaType: resolvedType,
      })
    : null;
  const name = performer?.performerName || performer?.name || 'this performer';
  const isEvents = pageKey === 'upcoming-events' || pageKey === 'events';

  return [
    buildWebPageSchema({
      '@type': 'CollectionPage',
      name: meta?.metaTitle || meta?.h1 || name,
      description: meta?.metaDescription || meta?.description,
      url: pageUrl,
      about: performerSchema ? { '@id': performerSchema['@id'] } : undefined,
    }),
    performerSchema,
    buildBreadcrumbListSchema(breadcrumbs, rootUrl),
    buildFaqPageSchema(faqs),
    isEvents
      ? buildUpcomingEventsItemListSchema({
          upcomingEvents,
          rootUrl,
          name: `Upcoming ${name} events`,
        })
      : buildVenuesItemListSchema({
          venues,
          rootUrl,
          name: `Venues for ${name}`,
        }),
  ].filter(Boolean);
};
